import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../utils/api";
import toast from "react-hot-toast";
import { useAuth } from "../../App";

const EBEK_TRIP_OPTIONS = [
  "OET Examination",
  "Cambridge Examination",
  "Other Examination",
  "Marketing",
  "Petty Cash",
  "Others",
];

const EBEKMED_TRIP_OPTIONS = [
  "INC Training",
  "OSCE Training",
  "TOT Training",
  "Other Training",
  "Institution Visit",
  "Petty Cash",
  "Others",
];

const EBEKMED_TRAINING_TYPES = ["INC Training", "OSCE Training", "TOT Training", "Other Training"];

const EMPTY_FORM = {
  titleOption: "",
  title: "",
  purpose: "",
  destination: "",
  start_date: "",
  end_date: "",
  exam_details: "",
  venue: "",
  exam_location: "",
  // Ebekmed-specific fields
  institution_name: "",
  participant_count: "",
};

export default function EmployeeTrips() {
  const { user } = useAuth();
  const isEbek = user?.company === "ebek";

  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [submittingId, setSubmittingId] = useState(null);  // trip id being submitted for reimbursement

  const handleSubmitReimbursement = async (tripId, tripTitle, amount) => {
    if (!window.confirm(`Submit "${tripTitle}" for reimbursement?\nAmount to be reimbursed: ₹${parseFloat(amount||0).toLocaleString("en-IN")}`)) return;
    setSubmittingId(tripId);
    try {
      await API.post(`/reimbursements/trips/${tripId}/submit`);
      toast.success("Reimbursement request submitted! Admin will process it soon.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to submit reimbursement");
    } finally {
      setSubmittingId(null);
    }
  };

  const load = () => {
    setLoading(true);
    API.get("/trips/my").then(r => { setTrips(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const showExamFields = isEbek && form.titleOption === "Other Examination";

  const getTitle = () => {
    if (form.titleOption === "Others") return form.title;
    if (form.titleOption) return form.titleOption;
    return form.title;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const resolvedTitle = getTitle();
    if (!resolvedTitle) { toast.error("Please enter or select a trip title"); return; }
    setSaving(true);
    try {
      await API.post("/trips", {
        title            : resolvedTitle,
        purpose          : form.purpose,
        destination      : form.destination,
        start_date       : form.start_date,
        end_date         : form.end_date,
        exam_details     : showExamFields ? form.exam_details  : undefined,
        venue            : showExamFields ? form.venue         : undefined,
        exam_location    : showExamFields ? form.exam_location : undefined,
        institution_name : form.institution_name || undefined,
        participant_count: form.participant_count ? parseInt(form.participant_count) : undefined,
      });
      toast.success("Trip created successfully!");
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to create trip");
    } finally { setSaving(false); }
  };

  const openModal = () => { setForm(EMPTY_FORM); setShowModal(true); };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 className="page-title">My Trips</h1>
          <p className="page-subtitle">Manage all your travel expense reports</p>
        </div>
        <button className="btn btn-primary" onClick={openModal}>✈️ New Trip</button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>Loading trips...</div>
      ) : trips.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div style={{ fontSize: 56 }}>✈️</div>
            <h3>No trips yet</h3>
            <p>Create your first trip to start logging expenses</p>
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={openModal}>
              Create First Trip
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          {trips.map(trip => {
            const balance = parseFloat(trip.advance_amount || 0) - parseFloat(trip.total_expenses || 0);
            const reimburseAmount = Math.max(0, parseFloat(trip.total_expenses||0) - parseFloat(trip.advance_amount||0));
            const rStatus = trip.reimbursement_status;

            // Reimbursement status pill config
            const rStatusConfig = {
              pending:           { label: "Not submitted",      color: "#9ca3af", bg: "#f3f4f6"  },
              submitted:         { label: "⏳ Submitted",        color: "#92400e", bg: "#fef3c7"  },
              under_review:      { label: "🔍 Under Review",    color: "#1d4ed8", bg: "#dbeafe"  },
              approved:          { label: "✅ Approved",         color: "#065f46", bg: "#d1fae5"  },
              payment_initiated: { label: "🏦 Payment Sent",    color: "#6d28d9", bg: "#ede9fe"  },
              paid:              { label: "💰 Paid",             color: "#065f46", bg: "#bbf7d0"  },
            };
            const rc = rStatusConfig[rStatus] || rStatusConfig.pending;

            const isMobile = window.innerWidth <= 600;
            return (
              <div key={trip.id} className="card" style={{ padding: 0 }}>
                <div style={{ padding: "16px 20px", display: "flex", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
                      <h3 style={{ fontSize: 17, fontWeight: 700, color: "#1f2937" }}>{trip.title}</h3>
                      <span className={`badge badge-${trip.status}`}>{trip.status}</span>
                      {/* Reimbursement status — only show on settled trips */}
                      {trip.status === "settled" && (
                        <span style={{
                          fontSize: 12, fontWeight: 700, padding: "3px 10px",
                          borderRadius: 20, color: rc.color, background: rc.bg,
                          border: `1px solid ${rc.color}33`
                        }}>
                          {rc.label}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: "#6b7280" }}>
                      {trip.purpose && <span>📋 {trip.purpose} &nbsp;&bull;&nbsp; </span>}
                      {trip.destination && <span>📍 {trip.destination} &nbsp;&bull;&nbsp; </span>}
                      <span>📅 {new Date(trip.start_date).toLocaleDateString("en-IN")}
                        {trip.end_date && ` — ${new Date(trip.end_date).toLocaleDateString("en-IN")}`}
                      </span>
                    </div>
                    {/* Payment reference — show once paid */}
                    {rStatus === "paid" && trip.payment_reference && (
                      <div style={{ fontSize: 12, color: "#059669", marginTop: 4, fontWeight: 600 }}>
                        💳 Ref: {trip.payment_reference}
                        {trip.reimbursement_date && ` · ${new Date(trip.reimbursement_date).toLocaleDateString("en-IN")}`}
                      </div>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: isMobile ? 12 : 20, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, marginBottom: 2 }}>ADVANCE</div>
                      <div style={{ fontSize: isMobile ? 14 : 17, fontWeight: 800, color: "#d97706" }}>
                        ₹{parseFloat(trip.advance_amount||0).toLocaleString("en-IN")}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, marginBottom: 2 }}>SPENT</div>
                      <div style={{ fontSize: isMobile ? 14 : 17, fontWeight: 800, color: "#E31E24" }}>
                        ₹{parseFloat(trip.total_expenses||0).toLocaleString("en-IN")}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, marginBottom: 2 }}>BALANCE</div>
                      <div style={{ fontSize: isMobile ? 14 : 17, fontWeight: 800, color: balance >= 0 ? "#059669" : "#DC2626" }}>
                        {balance >= 0 ? "+" : ""}₹{Math.abs(balance).toLocaleString("en-IN")}
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <Link to={`/trips/${trip.id}`} className="btn btn-primary btn-sm">
                        View Details →
                      </Link>
                      {/* Submit for Reimbursement — only when settled + pending + amount > 0 */}
                      {trip.status === "settled" && rStatus === "pending" && reimburseAmount > 0 && (
                        <button
                          className="btn btn-sm"
                          style={{ background: "#059669", color: "white", border: "none", fontWeight: 700, fontSize: 12 }}
                          disabled={submittingId === trip.id}
                          onClick={() => handleSubmitReimbursement(trip.id, trip.title, reimburseAmount)}
                        >
                          {submittingId === trip.id ? "Submitting..." : `💰 Request ₹${reimburseAmount.toLocaleString("en-IN")}`}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New Trip Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: 18, fontWeight: 700 }}>✈️ Create New Trip</h2>
              <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#9ca3af" }}>×</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">

                {/* Both Ebek and Ebekmed: Dropdown for trip type */}
                <div className="form-group">
                  <label className="form-label">Trip / Purpose *</label>
                  <select className="form-control" required
                    value={form.titleOption}
                    onChange={e => setForm({ ...form, titleOption: e.target.value, title: "", exam_details: "", venue: "", exam_location: "" })}>
                    <option value="">— Select category —</option>
                    {(isEbek ? EBEK_TRIP_OPTIONS : EBEKMED_TRIP_OPTIONS).map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {/* "Others" → free text title */}
                {form.titleOption === "Others" && (
                  <div className="form-group">
                    <label className="form-label">Trip Title *</label>
                    <input className="form-control" placeholder="Describe this trip"
                      value={form.title} onChange={e => setForm({...form, title: e.target.value})} required />
                  </div>
                )}

                {/* Ebek "Other EXAM Expenses" → exam detail fields */}
                {showExamFields && (
                  <div style={{ border:"1px solid #E5E7EB", borderRadius:10, padding:"14px 16px", marginBottom:8, background:"#F9FAFB" }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#374151", marginBottom:12 }}>📝 Exam Details</div>
                    <div className="form-group" style={{ marginBottom:12 }}>
                      <label className="form-label">Exam Details</label>
                      <input className="form-control" placeholder="e.g. OET Nursing – Module B"
                        value={form.exam_details} onChange={e => setForm({...form, exam_details: e.target.value})} />
                    </div>
                    <div className="form-group" style={{ marginBottom:12 }}>
                      <label className="form-label">Venue</label>
                      <input className="form-control" placeholder="e.g. British Council Chennai"
                        value={form.venue} onChange={e => setForm({...form, venue: e.target.value})} />
                    </div>
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label className="form-label">Exam Location</label>
                      <input className="form-control" placeholder="e.g. Chennai, Tamil Nadu"
                        value={form.exam_location} onChange={e => setForm({...form, exam_location: e.target.value})} />
                    </div>
                  </div>
                )}

                {/* ── Ebekmed: Training-specific fields ── */}
                {!isEbek && EBEKMED_TRAINING_TYPES.includes(form.titleOption) && (
                  <div style={{ border:"1px solid #E5E7EB", borderRadius:10, padding:"14px 16px", marginBottom:8, background:"#F9FAFB" }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#374151", marginBottom:12 }}>🏫 Training Details</div>
                    <div className="form-group" style={{ marginBottom:12 }}>
                      <label className="form-label">Institution Name *</label>
                      <input className="form-control" placeholder="e.g. Govt. Medical College, Chennai"
                        value={form.institution_name}
                        onChange={e => setForm({...form, institution_name: e.target.value})} required />
                    </div>
                    <div className="form-group" style={{ marginBottom:12 }}>
                      <label className="form-label">Location *</label>
                      <input className="form-control" placeholder="e.g. Chennai, Tamil Nadu"
                        value={form.destination}
                        onChange={e => setForm({...form, destination: e.target.value})} required />
                    </div>
                    <div className="form-group" style={{ marginBottom:12 }}>
                      <label className="form-label">Date of Training *</label>
                      <input type="date" className="form-control"
                        value={form.start_date}
                        onChange={e => setForm({...form, start_date: e.target.value})} required />
                    </div>
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label className="form-label">No. of Participants</label>
                      <input className="form-control" type="number" min="0" placeholder="e.g. 30"
                        value={form.participant_count}
                        onChange={e => setForm({...form, participant_count: e.target.value})} />
                    </div>
                  </div>
                )}

                {/* ── Ebekmed: Institution Visit ── */}
                {!isEbek && form.titleOption === "Institution Visit" && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Location *</label>
                      <input className="form-control" placeholder="e.g. College / Hospital name & city"
                        value={form.destination}
                        onChange={e => setForm({...form, destination: e.target.value})} required />
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                      <div className="form-group">
                        <label className="form-label">Start Date *</label>
                        <input type="date" className="form-control"
                          value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} required />
                      </div>
                      <div className="form-group">
                        <label className="form-label">End Date</label>
                        <input type="date" className="form-control"
                          value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} />
                      </div>
                    </div>
                  </>
                )}

                {/* ── Petty Cash (both Ebek and Ebekmed) ── */}
                {form.titleOption === "Petty Cash" && (
                  <div style={{ border:"1px solid #E5E7EB", borderRadius:10, padding:"14px 16px", marginBottom:8, background:"#F9FAFB" }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#374151", marginBottom:12 }}>🏢 Center Details</div>
                    <div className="form-group" style={{ marginBottom:12 }}>
                      <label className="form-label">Center Name *</label>
                      <input className="form-control" placeholder="e.g. Ebek Med Training Center"
                        value={form.institution_name}
                        onChange={e => setForm({...form, institution_name: e.target.value})} required />
                    </div>
                    <div className="form-group" style={{ marginBottom:0 }}>
                      <label className="form-label">Location *</label>
                      <input className="form-control" placeholder="e.g. Chennai, Tamil Nadu"
                        value={form.destination}
                        onChange={e => setForm({...form, destination: e.target.value})} required />
                    </div>
                  </div>
                )}

                {/* ── Ebekmed: Others + Ebek: generic destination/date ── */}
                {(isEbek || form.titleOption === "Others") && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Purpose</label>
                      <input className="form-control" placeholder="e.g. Client meetings, Training program"
                        value={form.purpose} onChange={e => setForm({...form, purpose: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Destination</label>
                      <input className="form-control" placeholder="e.g. Mumbai, Maharashtra"
                        value={form.destination} onChange={e => setForm({...form, destination: e.target.value})} />
                    </div>
                  </>
                )}

                {/* Start/End date for Ebek and Ebekmed "Others" trips */}
                {(isEbek || form.titleOption === "Others") && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <div className="form-group">
                      <label className="form-label">Start Date *</label>
                      <input type="date" className="form-control"
                        value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">End Date</label>
                      <input type="date" className="form-control"
                        value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} />
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Creating..." : "✈️ Create Trip"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
